import { type ChangeEvent, type DragEvent, type FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { SearchInput, FilterBar, StatusBadge, Drawer, EmptyState, SortSelect, useToast } from '@/components/base';
import { useAppContext } from '@/contexts/AppContext';
import type { Client, CreateClientInput } from '@/types/client';
import { useAddressLookup } from '@/hooks/useAddressLookup';
import { useClientDuplicateCheck } from '@/hooks/useClientDuplicateCheck';
import {
  formatClientDuplicateSummary,
  getProbableDuplicates,
  getStrongDuplicate,
} from '@/lib/clientDuplicateDecision';
import { compareDate, compareText, stableSort } from '@/lib/listSorting';

const createEmptyClientForm = () => ({
  type: 'pf' as 'pf' | 'pj',
  name: '', cpfCnpj: '', rg: '', orgExpedidor: '', nacionalidade: '',
  estadoCivil: '', profissao: '', dataNascimento: '',
  phone: '', email: '',
  cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', estado: '',
  nomeMae: '', nomePai: '',
  representanteLegal: '', cpfRepresentante: '', cargoRepresentante: '',
  responsible: '', origin: '', driveFolder: '',
  observations: '',
});

type ImportStatus = 'upload' | 'preview' | 'result';

type ParsedClientImportRow = {
  lineNumber: number;
  values: Record<string, string>;
  errors: string[];
};

type ImportSummary = {
  totalRead: number;
  imported: number;
  failed: number;
  ignored: number;
};

const clientImportColumns = [
  'nome',
  'cpf_cnpj',
  'rg',
  'estado_civil',
  'profissao',
  'endereco',
  'cidade',
  'estado',
  'telefone',
  'email',
  'observacoes',
  'origem',
  'responsavel',
] as const;

const clientImportHeaderLabels: Record<(typeof clientImportColumns)[number], string> = {
  nome: 'Nome',
  cpf_cnpj: 'CPF/CNPJ',
  rg: 'RG',
  estado_civil: 'Estado civil',
  profissao: 'Profissão',
  endereco: 'Endereço',
  cidade: 'Cidade',
  estado: 'Estado',
  telefone: 'Telefone',
  email: 'E-mail',
  observacoes: 'Observações',
  origem: 'Origem',
  responsavel: 'Responsável',
};

const normalizeImportKey = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const csvHeaderAliases: Record<string, (typeof clientImportColumns)[number]> = {
  nome: 'nome',
  name: 'nome',
  cpf_cnpj: 'cpf_cnpj',
  cpfcnpj: 'cpf_cnpj',
  rg: 'rg',
  estado_civil: 'estado_civil',
  estadocivil: 'estado_civil',
  profissao: 'profissao',
  endereco: 'endereco',
  logradouro: 'endereco',
  address: 'endereco',
  cidade: 'cidade',
  city: 'cidade',
  estado: 'estado',
  state: 'estado',
  telefone: 'telefone',
  phone: 'telefone',
  email: 'email',
  observacoes: 'observacoes',
  notes: 'observacoes',
  origem: 'origem',
  source: 'origem',
  responsavel: 'responsavel',
  responsible: 'responsavel',
};

const csvModelContent = `\ufeff${clientImportColumns.join(';')}\r\n`;

const downloadTextFile = (filename: string, content: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const normalizeCsvDelimiter = (content: string) => {
  const firstLine = content
    .replace(/^\uFEFF/, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .find((line) => line.trim().length > 0);

  if (!firstLine) {
    return ';';
  }

  const semicolons = (firstLine.match(/;/g) ?? []).length;
  const commas = (firstLine.match(/,/g) ?? []).length;

  return semicolons >= commas ? ';' : ',';
};

const parseCsvContent = (content: string) => {
  const delimiter = normalizeCsvDelimiter(content);
  const normalized = content.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows: string[][] = [];
  let currentCell = '';
  let currentRow: string[] = [];
  let inQuotes = false;

  const pushCell = () => {
    currentRow.push(currentCell);
    currentCell = '';
  };

  const pushRow = () => {
    if (currentRow.some((cell) => cell.trim() !== '')) {
      rows.push(currentRow);
    }
    currentRow = [];
  };

  for (let index = 0; index < normalized.length; index += 1) {
    const char = normalized[index];
    const nextChar = normalized[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentCell += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (!inQuotes && char === delimiter) {
      pushCell();
      continue;
    }

    if (!inQuotes && char === '\n') {
      pushCell();
      pushRow();
      continue;
    }

    currentCell += char;
  }

  pushCell();
  pushRow();
  return rows;
};

const normalizeCsvValue = (value: string | undefined) => String(value ?? '').trim();

const buildCsvHeaderMap = (headers: string[]) => {
  const headerMap = new Map<string, string>();

  headers.forEach((header, index) => {
    const normalized = normalizeImportKey(header);
    const mapped = csvHeaderAliases[normalized] ?? null;

    if (mapped && !headerMap.has(mapped)) {
      headerMap.set(mapped, String(index));
    }
  });

  return headerMap;
};

const buildClientImportRows = (content: string) => {
  const rows = parseCsvContent(content);

  if (rows.length === 0) {
    return { headers: [] as string[], rows: [] as ParsedClientImportRow[], delimiter: ';' as ';' | ',' };
  }

  const [headers, ...dataRows] = rows;
  const headerMap = buildCsvHeaderMap(headers);

  const parsedRows = dataRows.map((row, index) => {
    const values = clientImportColumns.reduce<Record<string, string>>((acc, column) => {
      const headerIndex = headerMap.get(column);
      const cellIndex = headerIndex === undefined ? -1 : Number(headerIndex);
      acc[column] = cellIndex >= 0 ? normalizeCsvValue(row[cellIndex] ?? '') : '';
      return acc;
    }, {});

    const errors: string[] = [];
    if (!values.nome) {
      errors.push('Nome obrigatório.');
    }

    const hasAnyValue = Object.values(values).some((value) => value.trim() !== '');

    if (!hasAnyValue) {
      errors.push('Linha vazia ignorada.');
    }

    return {
      lineNumber: index + 2,
      values,
      errors,
    };
  });

  return { headers, rows: parsedRows, delimiter: normalizeCsvDelimiter(content) };
};

const inferPersonType = (cpfCnpj: string) => {
  const digits = cpfCnpj.replace(/\D/g, '');
  if (digits.length === 14) {
    return 'PJ' as const;
  }

  return 'PF' as const;
};

const buildImportPayload = (row: ParsedClientImportRow): CreateClientInput | null => {
  const name = row.values.nome.trim();
  if (!name) {
    return null;
  }

  return {
    personType: inferPersonType(row.values.cpf_cnpj),
    name,
    cpfCnpj: row.values.cpf_cnpj || undefined,
    rg: row.values.rg || undefined,
    civilStatus: row.values.estado_civil || undefined,
    profession: row.values.profissao || undefined,
    street: row.values.endereco || undefined,
    city: row.values.cidade || undefined,
    state: row.values.estado || undefined,
    phone: row.values.telefone || undefined,
    email: row.values.email || undefined,
    origin: row.values.origem || undefined,
    responsible: row.values.responsavel || undefined,
    notes: row.values.observacoes || undefined,
  };
};

const ClientsPage: FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { clients, createClient, updateClient, deleteClient, clientsLoading, clientsError } = useAppContext();
  const { lookupAddress, isLookingUpAddress } = useAddressLookup();
  const { checkClientDuplicates } = useClientDuplicateCheck();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState('name_asc');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showNewClientDrawer, setShowNewClientDrawer] = useState(false);
  const [newClientTab, setNewClientTab] = useState<'basicos' | 'contato' | 'endereco' | 'documentos' | 'observacoes'>('basicos');
  const [importStep, setImportStep] = useState<ImportStatus>('upload');
  const [importFileName, setImportFileName] = useState('');
  const [importRows, setImportRows] = useState<ParsedClientImportRow[]>([]);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [isImportingClients, setIsImportingClients] = useState(false);
  const [isSavingClient, setIsSavingClient] = useState(false);
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [deletingClientId, setDeletingClientId] = useState<string | null>(null);
  const [clientOverview, setClientOverview] = useState<any>(null);
  const [overviewTab, setOverviewTab] = useState('Resumo');
  const importFileInputRef = useRef<HTMLInputElement | null>(null);

  const [newClient, setNewClient] = useState(createEmptyClientForm);

  useEffect(() => {
    if (!selectedClient) {
      setClientOverview(null);
      return;
    }
    setOverviewTab('Resumo');
    void fetch(`/api/clients/${encodeURIComponent(selectedClient.id)}?view=overview`, { credentials: 'same-origin' })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data.success) throw new Error(data.error || 'Não foi possível carregar a visão 360.');
        setClientOverview(data.overview);
      })
      .catch((error) => setClientOverview({ complete: false, warning: error instanceof Error ? error.message : 'Não foi possível carregar a visão 360.', failedSources: [] }));
  }, [selectedClient]);


  const validImportRows = useMemo(
    () => importRows.filter((row) => row.errors.length === 0 && row.values.nome.trim() !== ''),
    [importRows]
  );
  const skippedImportRows = useMemo(
    () => importRows.filter((row) => row.errors.length > 0 || row.values.nome.trim() === ''),
    [importRows]
  );

  const editableClientStatuses = ['Ativo', 'Inativo'];
  const filteredClients = stableSort(clients.filter((c) => {
    if (search && !c.name.toLowerCase().includes(search.toLowerCase()) && !c.cpfCnpj.includes(search) && !c.phone.includes(search) && !c.email.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter && c.status !== statusFilter) return false;
    return true;
  }), (left, right) => {
    if (sort === 'name_desc') return compareText(left.name, right.name, 'desc');
    if (sort === 'created_asc') return compareDate(left.createdAt, right.createdAt, 'asc');
    if (sort === 'updated_desc') return compareDate(left.updatedAt, right.updatedAt, 'desc');
    if (sort === 'created_desc') return compareDate(left.createdAt, right.createdAt, 'desc');
    return compareText(left.name, right.name);
  });

  const getClientPayload = (): CreateClientInput => ({
    personType: newClient.type === 'pf' ? 'PF' : 'PJ',
    name: newClient.name.trim(),
    cpfCnpj: newClient.cpfCnpj,
    rg: newClient.rg,
    issuingAuthority: newClient.orgExpedidor,
    nationality: newClient.nacionalidade,
    civilStatus: newClient.estadoCivil,
    profession: newClient.profissao,
    birthDate: newClient.dataNascimento,
    phone: newClient.phone,
    email: newClient.email,
    zipCode: newClient.cep,
    street: newClient.logradouro,
    number: newClient.numero,
    complement: newClient.complemento,
    neighborhood: newClient.bairro,
    city: newClient.cidade,
    state: newClient.estado,
    motherName: newClient.nomeMae,
    fatherName: newClient.nomePai,
    legalRepresentativeName: newClient.representanteLegal,
    legalRepresentativeCpf: newClient.cpfRepresentante,
    legalRepresentativeRole: newClient.cargoRepresentante,
    origin: newClient.origin,
    responsible: newClient.responsible,
    driveFolder: newClient.driveFolder,
    notes: newClient.observations,
    status: editingClientId && clients.find((client) => client.id === editingClientId)?.status === 'Inativo' ? 'inactive' : 'active',
  });

  const closeClientForm = () => {
    if (isSavingClient) return;
    setShowNewClientDrawer(false);
    setEditingClientId(null);
    setNewClient(createEmptyClientForm());
    setNewClientTab('basicos');
  };

  const handleOpenNewClient = () => {
    setEditingClientId(null);
    setNewClient(createEmptyClientForm());
    setNewClientTab('basicos');
    setShowNewClientDrawer(true);
  };

  const handleEditClient = (client: Client) => {
    setNewClient({
      type: client.personType?.toUpperCase() === 'PJ' ? 'pj' : 'pf',
      name: client.name,
      cpfCnpj: client.cpfCnpj,
      rg: client.rg || '',
      orgExpedidor: client.issuingAuthority || '',
      nacionalidade: client.nationality || '',
      estadoCivil: client.civilStatus || '',
      profissao: client.profession || '',
      dataNascimento: client.birthDate || '',
      phone: client.phone,
      email: client.email,
      cep: client.zipCode || '',
      logradouro: client.street || '',
      numero: client.number || '',
      complemento: client.complement || '',
      bairro: client.neighborhood || '',
      cidade: client.cityName || client.city.split(' - ')[0] || '',
      estado: client.state || '',
      nomeMae: client.motherName || '',
      nomePai: client.fatherName || '',
      representanteLegal: client.legalRepresentativeName || '',
      cpfRepresentante: client.legalRepresentativeCpf || '',
      cargoRepresentante: client.legalRepresentativeRole || '',
      responsible: client.responsible || '',
      origin: client.origin || '',
      driveFolder: client.driveFolder || '',
      observations: client.notes || '',
    });
    setEditingClientId(client.id);
    setSelectedClient(null);
    setNewClientTab('basicos');
    setShowNewClientDrawer(true);
  };

  const handleLookupZipCode = async () => {
    const digits =
      newClient.cep.replace(
        /\D/g,
        ''
      );

    if (!digits) {
      addToast(
        'Informe o CEP.',
        'warning'
      );

      return;
    }

    if (
      digits.length !== 8
    ) {
      addToast(
        'O CEP deve conter 8 dígitos.',
        'warning'
      );

      return;
    }

    try {
      const address =
        await lookupAddress(
          digits
        );

      setNewClient(
        (
          current
        ) => ({
          ...current,

          cep:
            address.zipCode,

          logradouro:
            address.street ||
            current.logradouro,

          bairro:
            address.neighborhood ||
            current.bairro,

          cidade:
            address.city ||
            current.cidade,

          estado:
            address.state ||
            current.estado,
        })
      );

      addToast(
        'Endereço localizado pelo CEP.',
        'success'
      );
    } catch (error) {
      addToast(
        error instanceof Error
          ? error.message
          : 'Não foi possível consultar o CEP.',
        'error'
      );
    }
  };

  const handleSaveClient = async () => {
    if (!newClient.name.trim()) {
      addToast(
        'Preencha o nome do cliente.',
        'warning'
      );

      return;
    }

    setIsSavingClient(true);

    try {
      const payload =
        getClientPayload();

      const duplicateCheck =
        await checkClientDuplicates(
          payload,
          editingClientId
        );

      const strongDuplicate =
        getStrongDuplicate(
          duplicateCheck.duplicates
        );

      if (
        strongDuplicate
      ) {
        addToast(
          `Este CPF/CNPJ já pertence ao cliente ${strongDuplicate.name}.`,
          'error'
        );

        return;
      }

      const probableDuplicates =
        getProbableDuplicates(
          duplicateCheck.duplicates
        );

      if (
        probableDuplicates.length >
        0
      ) {
        const summary =
          formatClientDuplicateSummary(
            probableDuplicates
          );

        const confirmed =
          window.confirm(
            `Encontramos possível cadastro existente:\n\n${summary}\n\nDeseja cadastrar mesmo assim?`
          );

        if (
          !confirmed
        ) {
          addToast(
            'Cadastro cancelado para evitar possível duplicidade.',
            'warning'
          );

          return;
        }
      }

      if (
        editingClientId
      ) {
        await updateClient(
          editingClientId,
          payload
        );

        addToast(
          'Cliente atualizado com sucesso',
          'success'
        );
      } else {
        await createClient(
          payload
        );

        addToast(
          'Cliente cadastrado com sucesso',
          'success'
        );
      }

      setShowNewClientDrawer(
        false
      );

      setEditingClientId(
        null
      );

      setNewClient(
        createEmptyClientForm()
      );

      setNewClientTab(
        'basicos'
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Não foi possível salvar o cliente.';

      addToast(
        message,
        'error'
      );
    } finally {
      setIsSavingClient(
        false
      );
    }
  };

  const handleDeleteClient = async (client: Client) => {
    if (deletingClientId) return;
    const confirmed = window.confirm('Tem certeza que deseja excluir este cliente? Esta ação removerá o cliente da listagem, mas manterá o registro no banco para auditoria.');
    if (!confirmed) return;

    setDeletingClientId(client.id);
    try {
      await deleteClient(client.id);
      if (selectedClient?.id === client.id) setSelectedClient(null);
      addToast('Cliente excluído com sucesso', 'success');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível excluir o cliente.';
      addToast(message, 'error');
    } finally {
      setDeletingClientId(null);
    }
  };

  const resetImportState = () => {
    setImportStep('upload');
    setImportFileName('');
    setImportRows([]);
    setImportErrors([]);
    setImportSummary(null);
    setIsImportingClients(false);
    if (importFileInputRef.current) {
      importFileInputRef.current.value = '';
    }
  };

  const handleOpenImportFilePicker = () => {
    importFileInputRef.current?.click();
  };

  const processImportFile = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      addToast('Selecione um arquivo CSV válido.', 'warning');
      return;
    }

    try {
      const text = await file.text();
      const parsed = buildClientImportRows(text);
      const validationErrors = parsed.rows.flatMap((row) =>
        row.errors.map((error) => `Linha ${row.lineNumber}: ${error}`)
      );

      if (parsed.rows.length === 0) {
        addToast('O arquivo CSV não possui linhas de dados.', 'warning');
        return;
      }

      setImportFileName(file.name);
      setImportRows(parsed.rows);
      setImportErrors(validationErrors);
      setImportSummary(null);
      setImportStep('preview');
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Não foi possível ler o arquivo CSV.', 'error');
    }
  };

  const handleImportFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    await processImportFile(file);
    event.target.value = '';
  };

  const handleImportFileDrop = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    const file = event.dataTransfer.files?.[0];
    if (!file) {
      return;
    }

    await processImportFile(file);
  };

  const handleDownloadImportModel = () => {
    downloadTextFile('modelo-importacao-clientes-bm-legal-os.csv', csvModelContent, 'text/csv;charset=utf-8');
  };

  const handleConfirmImport = async () => {
    if (isImportingClients) {
      return;
    }

    setIsImportingClients(true);

    const summary: ImportSummary = {
      totalRead: importRows.length,
      imported: 0,
      failed: 0,
      ignored: skippedImportRows.length,
    };
    const validationErrors: string[] = [];

    try {
      for (const row of validImportRows) {
        const payload = buildImportPayload(row);

        if (!payload) {
          summary.failed += 1;
          validationErrors.push(`Linha ${row.lineNumber}: Nome obrigatório.`);
          continue;
        }

        try {
          await createClient(payload);
          summary.imported += 1;
        } catch (error) {
          summary.failed += 1;
          const message = error instanceof Error ? error.message : 'Falha desconhecida ao importar cliente.';
          validationErrors.push(`Linha ${row.lineNumber}: ${message}`);
        }
      }

      setImportErrors(validationErrors);
      setImportSummary(summary);
      setImportStep('result');
      addToast(
        `${summary.imported} cliente(s) importado(s), ${summary.failed} falha(s) e ${summary.ignored} linha(s) ignorada(s).`,
        summary.failed > 0 ? 'warning' : 'success'
      );

    } finally {
      setIsImportingClients(false);
    }
  };

  const newClientTabs = [
    { key: 'basicos' as const, label: 'Dados Básicos', icon: 'ri-user-line' },
    { key: 'contato' as const, label: 'Contato', icon: 'ri-phone-line' },
    { key: 'endereco' as const, label: 'Endereço', icon: 'ri-map-pin-line' },
    { key: 'documentos' as const, label: 'Dados Documentos', icon: 'ri-file-list-line' },
    { key: 'observacoes' as const, label: 'Observações', icon: 'ri-sticky-note-line' },
  ];

  return (
    <AppLayout>
      <div className="p-4 md:p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-foreground-900">Clientes</h2>
            <p className="text-sm text-foreground-500 mt-0.5">{filteredClients.length} clientes cadastrados</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowImportModal(true)} className="inline-flex items-center gap-1.5 px-3 py-2 border border-background-200 rounded-lg text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-upload-line"></i> Importar Clientes
            </button>
            <button onClick={handleOpenNewClient} className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-500 text-background-50 rounded-lg text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-add-line"></i> Novo Cliente
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Buscar por nome, CPF/CNPJ, telefone ou e-mail..." className="sm:w-80" />
          <FilterBar filters={[{ key: 'status', label: 'Status', options: editableClientStatuses.map(s => ({ label: s, value: s })), value: statusFilter, onChange: setStatusFilter }]} />
          <SortSelect value={sort} onChange={setSort} options={[{ value: 'name_asc', label: 'A–Z' }, { value: 'name_desc', label: 'Z–A' }, { value: 'created_desc', label: 'Mais recentes' }, { value: 'created_asc', label: 'Mais antigos' }, { value: 'updated_desc', label: 'Atualizados recentemente' }]} />
        </div>

        {(clientsLoading || clientsError) && (
          <div aria-live="polite" className={`text-xs ${clientsError ? 'text-amber-700' : 'text-foreground-500'}`}>
            {clientsError || 'Atualizando clientes...'}
          </div>
        )}

        <div className="bg-background-50 border border-background-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Nome</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden md:table-cell">CPF/CNPJ</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">Telefone</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden xl:table-cell">E-mail</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap hidden lg:table-cell">Cidade</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Casos</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredClients.length === 0 ? (
                  <tr><td colSpan={8}><EmptyState icon="ri-team-line" title="Nenhum cliente encontrado" description="Ajuste os filtros ou cadastre um novo cliente." /></td></tr>
                ) : (
                  filteredClients.map((client) => (
                    <tr key={client.id} className="border-b border-background-100 hover:bg-background-50 transition-colors cursor-pointer" onClick={() => setSelectedClient(client)}>
                      <td className="px-4 py-3"><p className="text-sm font-medium text-foreground-800 whitespace-nowrap">{client.name}</p></td>
                      <td className="px-4 py-3 hidden md:table-cell"><span className="text-sm text-foreground-500 font-mono whitespace-nowrap">{client.cpfCnpj}</span></td>
                      <td className="px-4 py-3 hidden lg:table-cell"><span className="text-sm text-foreground-600 whitespace-nowrap">{client.phone}</span></td>
                      <td className="px-4 py-3 hidden xl:table-cell"><span className="text-sm text-foreground-500 whitespace-nowrap">{client.email}</span></td>
                      <td className="px-4 py-3 hidden lg:table-cell"><span className="text-sm text-foreground-600 whitespace-nowrap">{client.city}</span></td>
                      <td className="px-4 py-3 text-center"><span className="text-sm font-semibold text-foreground-800 whitespace-nowrap">{client.casesCount}</span></td>
                      <td className="px-4 py-3"><StatusBadge status={client.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <button onClick={(e) => { e.stopPropagation(); handleEditClient(client); }} disabled={deletingClientId === client.id} className="px-2 py-1 rounded-md text-xs font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"><i className="ri-edit-line mr-1"></i>Editar</button>
                          <button onClick={(e) => { e.stopPropagation(); handleDeleteClient(client); }} disabled={deletingClientId === client.id} className="px-2 py-1 rounded-md text-xs font-medium text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"><i className="ri-delete-bin-line mr-1"></i>{deletingClientId === client.id ? 'Excluindo...' : 'Excluir'}</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Client Detail Drawer */}
        <Drawer isOpen={!!selectedClient} onClose={() => setSelectedClient(null)} title="Ficha do Cliente"
          footer={
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={() => navigate('/cases')} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-add-line mr-1"></i> Criar Caso</button>
              <button onClick={() => navigate('/documents')} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-file-text-line mr-1"></i> Gerar Documentos</button>
              <button onClick={() => navigate('/tasks')} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-todo-line mr-1"></i> Criar Tarefa</button>
              <button onClick={() => navigate('/finance')} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-bank-card-line mr-1"></i> Registrar Pagamento</button>
              <button onClick={() => navigate('/messages')} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-chat-3-line mr-1"></i> Enviar Mensagem</button>
              {selectedClient?.driveFolder ? <a href={selectedClient.driveFolder} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-folder-open-line mr-1"></i> Abrir Pasta no Drive</a> : <button disabled className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-400 cursor-not-allowed whitespace-nowrap"><i className="ri-folder-open-line mr-1"></i> Pasta não configurada</button>}
              {selectedClient && <button onClick={() => handleEditClient(selectedClient)} disabled={deletingClientId === selectedClient.id} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"><i className="ri-edit-line mr-1"></i> Editar</button>}
              {selectedClient && <button onClick={() => handleDeleteClient(selectedClient)} disabled={deletingClientId === selectedClient.id} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"><i className="ri-delete-bin-line mr-1"></i> {deletingClientId === selectedClient.id ? 'Excluindo...' : 'Excluir'}</button>}
            </div>
          }
        >
          {selectedClient && (
            <div className="space-y-5">
              <div>
                <h4 className="text-base font-semibold text-foreground-900">{selectedClient.name}</h4>
                <div className="flex items-center gap-2 mt-2">
                  <StatusBadge status={selectedClient.status} />
                  {selectedClient.pendingPayments && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-medium">Pagamentos Pendentes</span>}
                  {selectedClient.pendingDocs && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium">Docs Pendentes</span>}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><p className="text-[10px] text-foreground-400 uppercase tracking-wider">CPF/CNPJ</p><p className="text-sm text-foreground-700 font-mono">{selectedClient.cpfCnpj}</p></div>
                <div><p className="text-[10px] text-foreground-400 uppercase tracking-wider">Telefone</p><p className="text-sm text-foreground-700">{selectedClient.phone}</p></div>
                <div><p className="text-[10px] text-foreground-400 uppercase tracking-wider">E-mail</p><p className="text-sm text-foreground-700">{selectedClient.email}</p></div>
                <div><p className="text-[10px] text-foreground-400 uppercase tracking-wider">Cidade</p><p className="text-sm text-foreground-700">{selectedClient.city}</p></div>
                <div><p className="text-[10px] text-foreground-400 uppercase tracking-wider">Responsável</p><p className="text-sm text-foreground-700">{selectedClient.responsible}</p></div>
                <div><p className="text-[10px] text-foreground-400 uppercase tracking-wider">Pasta Drive</p><p className="text-sm text-foreground-500 font-mono">{selectedClient.driveFolder}</p></div>
              </div>
              <div className="border-b border-background-200 flex gap-1 overflow-x-auto">
                {['Resumo', 'Dados', 'Casos', 'Documentos', 'Financeiro', 'Prazos', 'Tarefas', 'Comunicações', 'Histórico'].map((tab) => <button key={tab} type="button" onClick={() => setOverviewTab(tab)} className={`px-2 py-2 text-xs whitespace-nowrap cursor-pointer ${overviewTab === tab ? 'text-primary-700 border-b-2 border-primary-500 font-semibold' : 'text-foreground-500'}`}>{tab}</button>)}
              </div>
              {clientOverview && !clientOverview.complete && <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700">{clientOverview.warning} Dados válidos permanecem visíveis; a consolidação está parcial.</div>}
              {overviewTab === 'Resumo' && clientOverview && <div className="grid grid-cols-2 gap-2"><div className="p-2 rounded-lg bg-background-100"><p className="text-[10px] text-foreground-400">Casos ativos</p><p className="font-semibold">{clientOverview.indicators?.activeCases ?? 0}</p></div><div className="p-2 rounded-lg bg-background-100"><p className="text-[10px] text-foreground-400">Prazos próximos</p><p className="font-semibold">{clientOverview.indicators?.upcomingDeadlines ?? 0}</p></div><div className="p-2 rounded-lg bg-background-100"><p className="text-[10px] text-foreground-400">Tarefas pendentes</p><p className="font-semibold">{clientOverview.indicators?.pendingTasks ?? 0}</p></div><div className="p-2 rounded-lg bg-background-100"><p className="text-[10px] text-foreground-400">Financeiro pendente</p><p className="font-semibold">{clientOverview.indicators?.pendingFinancial ?? 0}</p></div></div>}
              {clientOverview && overviewTab !== 'Resumo' && overviewTab !== 'Dados' && <div className="space-y-1.5">{({ Casos: clientOverview.cases, Documentos: clientOverview.documents, Financeiro: clientOverview.financial, Prazos: clientOverview.deadlines, Tarefas: clientOverview.tasks, Comunicações: clientOverview.communications, Histórico: clientOverview.history } as Record<string, any[]>)[overviewTab]?.map((item) => <div key={item.id} className="p-2 rounded-lg bg-background-100 text-xs text-foreground-700">{item.title || item.subject || item.eventType || 'Registro'}<span className="ml-2 text-foreground-400">{item.status || item.dueDate || item.createdAt || ''}</span></div>) || <p className="text-xs text-foreground-400">Nenhum registro encontrado.</p>}</div>}
              <div><h5 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Documentos</h5>
                <div className="space-y-1.5">
                  {selectedClient.documents.map((doc) => (
                    <div key={doc.name} className="flex items-center justify-between py-1.5 px-2.5 rounded-lg bg-background-100"><span className="text-sm text-foreground-700">{doc.name}</span><span className={`text-xs font-medium ${doc.status === 'ok' ? 'text-emerald-600' : doc.status === 'pending' ? 'text-amber-600' : 'text-red-500'}`}>{doc.status === 'ok' ? 'OK' : doc.status === 'pending' ? 'Pendente' : 'Faltando'}</span></div>
                  ))}
                </div>
              </div>
              {selectedClient.cases.length > 0 && (
                <div><h5 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Casos</h5>
                  <div className="space-y-1.5">
                    {selectedClient.cases.map((c) => (<div key={c} className="flex items-center gap-2 text-sm text-foreground-700 py-1.5 px-2.5 rounded-lg bg-background-100"><i className="ri-scales-line text-primary-500"></i>{c}</div>))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <span className={`text-xs px-2 py-1 rounded-full border font-medium ${selectedClient.hasContract ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{selectedClient.hasContract ? 'Contrato assinado' : 'Sem contrato'}</span>
                <span className={`text-xs px-2 py-1 rounded-full border font-medium ${selectedClient.hasPowerAttorney ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>{selectedClient.hasPowerAttorney ? 'Procuração assinada' : 'Sem procuração'}</span>
              </div>
            </div>
          )}
        </Drawer>

        {/* New Client Drawer — expandido com abas */}
        <Drawer isOpen={showNewClientDrawer} onClose={closeClientForm} title={editingClientId ? 'Editar Cliente' : 'Novo Cliente'} width="w-[560px]"
          footer={
            <div className="flex items-center gap-2">
              <button onClick={closeClientForm} disabled={isSavingClient} className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">Cancelar</button>
              <button onClick={handleSaveClient} disabled={isSavingClient} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed">{isSavingClient ? 'Salvando...' : editingClientId ? 'Salvar Alterações' : 'Salvar Cliente'}</button>
            </div>
          }
        >
          <div className="space-y-4">
            {/* Tab navigation */}
            <div className="flex gap-1 bg-background-100 rounded-lg p-1 overflow-x-auto">
              {newClientTabs.map((tab) => (
                <button key={tab.key} onClick={() => setNewClientTab(tab.key)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${newClientTab === tab.key ? 'bg-background-50 text-foreground-800' : 'text-foreground-500 hover:text-foreground-700'}`}>
                  <i className={tab.icon}></i> {tab.label}
                </button>
              ))}
            </div>

            {/* Dados Básicos */}
            {newClientTab === 'basicos' && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <button onClick={() => setNewClient((p) => ({ ...p, type: 'pf' }))} className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${newClient.type === 'pf' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-background-200 text-foreground-600 hover:bg-background-100'}`}>Pessoa Física</button>
                  <button onClick={() => setNewClient((p) => ({ ...p, type: 'pj' }))} className={`flex-1 py-2 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${newClient.type === 'pj' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'border-background-200 text-foreground-600 hover:bg-background-100'}`}>Pessoa Jurídica</button>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">{newClient.type === 'pf' ? 'Nome Completo *' : 'Razão Social *'}</label>
                  <input type="text" value={newClient.name} onChange={(e) => setNewClient((p) => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">{newClient.type === 'pf' ? 'CPF' : 'CNPJ'}</label>
                    <input type="text" value={newClient.cpfCnpj} onChange={(e) => setNewClient((p) => ({ ...p, cpfCnpj: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" />
                  </div>
                  {newClient.type === 'pf' && (
                    <div>
                      <label className="block text-xs font-medium text-foreground-600 mb-1">RG</label>
                      <input type="text" value={newClient.rg} onChange={(e) => setNewClient((p) => ({ ...p, rg: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" />
                    </div>
                  )}
                </div>
                {newClient.type === 'pf' ? (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">Órgão Expedidor</label>
                        <input type="text" value={newClient.orgExpedidor} onChange={(e) => setNewClient((p) => ({ ...p, orgExpedidor: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">Nacionalidade</label>
                        <input type="text" value={newClient.nacionalidade} onChange={(e) => setNewClient((p) => ({ ...p, nacionalidade: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">Estado Civil</label>
                        <select value={newClient.estadoCivil} onChange={(e) => setNewClient((p) => ({ ...p, estadoCivil: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                          <option value="">Selecione...</option>
                          <option value="Solteiro(a)">Solteiro(a)</option>
                          <option value="Casado(a)">Casado(a)</option>
                          <option value="Divorciado(a)">Divorciado(a)</option>
                          <option value="Viúvo(a)">Viúvo(a)</option>
                          <option value="União Estável">União Estável</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">Profissão</label>
                        <input type="text" value={newClient.profissao} onChange={(e) => setNewClient((p) => ({ ...p, profissao: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-foreground-600 mb-1">Data de Nascimento</label>
                        <input type="text" placeholder="dd/mm/aaaa" value={newClient.dataNascimento} onChange={(e) => setNewClient((p) => ({ ...p, dataNascimento: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" />
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            )}

            {/* Contato */}
            {newClientTab === 'contato' && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-medium text-foreground-600 mb-1">Telefone *</label><input type="text" value={newClient.phone} onChange={(e) => setNewClient((p) => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                  <div><label className="block text-xs font-medium text-foreground-600 mb-1">E-mail</label><input type="email" value={newClient.email} onChange={(e) => setNewClient((p) => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                </div>
              </div>
            )}

            {/* Endereço */}
            {newClientTab === 'endereco' && (
              <div className="space-y-3">
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-foreground-600 mb-1">
                      CEP
                    </label>

                    <div className="flex gap-1.5">
                      <input
                        type="text"
                        value={newClient.cep}
                        maxLength={9}
                        placeholder="00000-000"
                        onChange={(e) =>
                          setNewClient((p) => ({
                            ...p,
                            cep: e.target.value,
                          }))
                        }
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void handleLookupZipCode();
                          }
                        }}
                        className="min-w-0 flex-1 px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300"
                      />

                      <button
                        type="button"
                        title="Buscar endereço pelo CEP"
                        aria-label="Buscar endereço pelo CEP"
                        disabled={isLookingUpAddress}
                        onClick={() =>
                          void handleLookupZipCode()
                        }
                        className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <i
                          className={
                            isLookingUpAddress
                              ? 'ri-loader-4-line animate-spin'
                              : 'ri-search-line'
                          }
                        ></i>
                      </button>
                    </div>
                  </div>
                  <div className="col-span-3"><label className="block text-xs font-medium text-foreground-600 mb-1">Logradouro</label><input type="text" value={newClient.logradouro} onChange={(e) => setNewClient((p) => ({ ...p, logradouro: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div><label className="block text-xs font-medium text-foreground-600 mb-1">Número</label><input type="text" value={newClient.numero} onChange={(e) => setNewClient((p) => ({ ...p, numero: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                  <div className="col-span-3"><label className="block text-xs font-medium text-foreground-600 mb-1">Complemento</label><input type="text" value={newClient.complemento} onChange={(e) => setNewClient((p) => ({ ...p, complemento: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="block text-xs font-medium text-foreground-600 mb-1">Bairro</label><input type="text" value={newClient.bairro} onChange={(e) => setNewClient((p) => ({ ...p, bairro: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                  <div><label className="block text-xs font-medium text-foreground-600 mb-1">Cidade</label><input type="text" value={newClient.cidade} onChange={(e) => setNewClient((p) => ({ ...p, cidade: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                  <div><label className="block text-xs font-medium text-foreground-600 mb-1">Estado</label>
                    <select value={newClient.estado} onChange={(e) => setNewClient((p) => ({ ...p, estado: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                      {['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'].map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Dados para Documentos */}
            {newClientTab === 'documentos' && (
              <div className="space-y-3">
                {newClient.type === 'pf' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-xs font-medium text-foreground-600 mb-1">Nome da Mãe</label><input type="text" value={newClient.nomeMae} onChange={(e) => setNewClient((p) => ({ ...p, nomeMae: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                    <div><label className="block text-xs font-medium text-foreground-600 mb-1">Nome do Pai</label><input type="text" value={newClient.nomePai} onChange={(e) => setNewClient((p) => ({ ...p, nomePai: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                  </div>
                )}
                {newClient.type === 'pj' && (
                  <>
                    <div className="p-2.5 bg-background-100 rounded-lg">
                      <p className="text-xs font-semibold text-foreground-600 mb-2">Representante Legal (PJ)</p>
                      <div className="space-y-2">
                        <div><label className="block text-[10px] font-medium text-foreground-500 mb-1">Nome</label><input type="text" value={newClient.representanteLegal} onChange={(e) => setNewClient((p) => ({ ...p, representanteLegal: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                        <div className="grid grid-cols-2 gap-2">
                          <div><label className="block text-[10px] font-medium text-foreground-500 mb-1">CPF</label><input type="text" value={newClient.cpfRepresentante} onChange={(e) => setNewClient((p) => ({ ...p, cpfRepresentante: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                          <div><label className="block text-[10px] font-medium text-foreground-500 mb-1">Cargo</label><input type="text" value={newClient.cargoRepresentante} onChange={(e) => setNewClient((p) => ({ ...p, cargoRepresentante: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" /></div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-medium text-foreground-600 mb-1">Responsável Interno</label>
                    <select value={newClient.responsible} onChange={(e) => setNewClient((p) => ({ ...p, responsible: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                      <option value="Vinícius Meschick">Vinícius Meschick</option>
                      <option value="Dalila">Dalila</option>
                    </select>
                  </div>
                  <div><label className="block text-xs font-medium text-foreground-600 mb-1">Origem</label>
                    <select value={newClient.origin} onChange={(e) => setNewClient((p) => ({ ...p, origin: e.target.value }))} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                      <option value="">Selecione...</option>
                      <option value="WhatsApp">WhatsApp</option>
                      <option value="Instagram">Instagram</option>
                      <option value="Indicação">Indicação</option>
                      <option value="Google Ads">Google Ads</option>
                      <option value="Site">Site Institucional</option>
                      <option value="Landing Page">Landing Page</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Link da Pasta do Drive</label>
                  <input type="text" value={newClient.driveFolder} onChange={(e) => setNewClient((p) => ({ ...p, driveFolder: e.target.value }))} placeholder="01_CLIENTES/Nome_Cliente" className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 focus:outline-none focus:border-primary-300" />
                </div>
                {newClient.type === 'pj' && (
                  <div className="rounded-lg border border-dashed border-background-200 bg-background-50 p-3 text-xs text-foreground-500">
                    Campos pessoais de pessoa física ficam ocultos para pessoa jurídica.
                  </div>
                )}
              </div>
            )}

            {/* Observações */}
            {newClientTab === 'observacoes' && (
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1">Observações Gerais</label>
                <textarea value={newClient.observations} onChange={(e) => setNewClient((p) => ({ ...p, observations: e.target.value }))} rows={5} className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-800 resize-none focus:outline-none focus:border-primary-300" placeholder="Informações relevantes sobre o cliente..."></textarea>
              </div>
            )}
          </div>
        </Drawer>

        {/* Import Modal */}
        <Drawer
          isOpen={showImportModal}
          onClose={() => {
            setShowImportModal(false);
            resetImportState();
          }}
          title="Importar Clientes em CSV"
          width="w-[720px]"
          footer={
            importStep === 'preview' || importStep === 'result' ? (
              <div className="flex items-center justify-between w-full gap-3">
                <p className="text-xs text-amber-600 font-medium">
                  <i className="ri-information-line"></i> Revise os dados antes de importar. A importação criará novos clientes no sistema.
                </p>
                <div className="flex items-center gap-2">
                  {importStep === 'preview' ? (
                    <>
                      <button
                        onClick={resetImportState}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => void handleConfirmImport()}
                        disabled={isImportingClients}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {isImportingClients ? 'Importando...' : 'Confirmar Importação'}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          setImportStep('upload');
                          setImportErrors([]);
                          setImportSummary(null);
                          setImportRows([]);
                          setImportFileName('');
                          if (importFileInputRef.current) {
                            importFileInputRef.current.value = '';
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Novo Arquivo
                      </button>
                      <button
                        onClick={() => {
                          setShowImportModal(false);
                          resetImportState();
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Fechar
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : undefined
          }
        >
          <input
            ref={importFileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleImportFileChange}
          />

          {importStep === 'upload' && (
            <div className="space-y-4">
              <div
                className="border-2 border-dashed border-background-300 rounded-xl p-8 text-center hover:border-primary-300 transition-colors cursor-pointer"
                onClick={handleOpenImportFilePicker}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => void handleImportFileDrop(event)}
              >
                <i className="ri-upload-cloud-2-line text-4xl text-foreground-300 mb-2"></i>
                <p className="text-sm font-medium text-foreground-600">Arraste e solte o arquivo CSV aqui ou clique para selecionar</p>
                <p className="text-xs text-foreground-400 mt-1">Nesta versão, a importação é apenas CSV. XLSX fica para uma sprint futura.</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleOpenImportFilePicker}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-file-upload-line mr-1"></i>Selecionar CSV
                </button>
                <button
                  type="button"
                  onClick={handleDownloadImportModel}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-download-line mr-1"></i>Baixar Modelo CSV
                </button>
              </div>
              <div className="p-3 bg-background-100 rounded-lg space-y-2">
                <p className="text-xs font-semibold text-foreground-600">Colunas esperadas:</p>
                <p className="text-xs text-foreground-500 font-mono">{clientImportColumns.join(', ')}</p>
                <p className="text-xs text-foreground-500">
                  Revise os dados antes de importar. A importação criará novos clientes no sistema.
                </p>
              </div>
            </div>
          )}

          {importStep === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground-800">Pré-visualização</p>
                  <p className="text-xs text-foreground-500">{importFileName} · {importRows.length} linha(s) lida(s)</p>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                  {validImportRows.length} válidas
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Total lido</p>
                  <p className="text-base font-semibold text-foreground-900">{importRows.length}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Válidos</p>
                  <p className="text-base font-semibold text-foreground-900">{validImportRows.length}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Com erro / ignorados</p>
                  <p className="text-base font-semibold text-foreground-900">{skippedImportRows.length}</p>
                </div>
              </div>

              {importErrors.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Erros de validação</p>
                  <ul className="space-y-1 text-xs text-amber-700 list-disc pl-4">
                    {importErrors.slice(0, 8).map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-background-100 rounded-lg p-3 max-h-64 overflow-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-background-200">
                      <th className="text-left py-1.5 px-2 font-semibold text-foreground-500">Linha</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-foreground-500">Nome</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-foreground-500">CPF/CNPJ</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-foreground-500">Telefone</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-foreground-500">Cidade</th>
                      <th className="text-left py-1.5 px-2 font-semibold text-foreground-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importRows.slice(0, 8).map((row) => (
                      <tr key={row.lineNumber} className="border-b border-background-100">
                        <td className="py-1.5 px-2 text-foreground-500">{row.lineNumber}</td>
                        <td className="py-1.5 px-2 text-foreground-700">{row.values.nome || '—'}</td>
                        <td className="py-1.5 px-2 text-foreground-600 font-mono">{row.values.cpf_cnpj || '—'}</td>
                        <td className="py-1.5 px-2 text-foreground-600">{row.values.telefone || '—'}</td>
                        <td className="py-1.5 px-2 text-foreground-600">{row.values.cidade || '—'}</td>
                        <td className="py-1.5 px-2 text-foreground-600">{row.errors.length > 0 ? 'Erro' : 'OK'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {importStep === 'result' && importSummary && (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground-800">Resultado da importação</p>
                  <p className="text-xs text-foreground-500">{importFileName}</p>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                  Concluído
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Lidas</p>
                  <p className="text-base font-semibold text-foreground-900">{importSummary.totalRead}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Importadas</p>
                  <p className="text-base font-semibold text-foreground-900">{importSummary.imported}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Falhas</p>
                  <p className="text-base font-semibold text-foreground-900">{importSummary.failed}</p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-3">
                  <p className="text-foreground-500">Ignoradas</p>
                  <p className="text-base font-semibold text-foreground-900">{importSummary.ignored}</p>
                </div>
              </div>

              {importErrors.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="text-xs font-semibold text-amber-800 mb-1">Falhas encontradas</p>
                  <ul className="space-y-1 text-xs text-amber-700 list-disc pl-4">
                    {importErrors.slice(0, 8).map((error) => (
                      <li key={error}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </Drawer>
      </div>
    </AppLayout>
  );
};

export default ClientsPage;
